import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Landmark, Paperclip, Plus, Receipt, Scissors } from 'lucide-react';
import { api } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDate } from '@/lib/dates';
import { formatRateHuman } from '@fint/money';
import { Attachments } from '@/features/common/Attachments';
import { Badge, Button, Card, CardContent, Money } from '@/features/common/ui';
import { RecordPaymentDrawer } from '@/features/payments/RecordPaymentDrawer';
import { PaymentTdsSheet, WriteOffSheet } from './CloseRemainderSheets';

/**
 * The payments against one invoice, each with its receipt.
 *
 * ── WHY ON THE INVOICE ──
 * An invoice paid in instalments - an advance, a milestone, the balance -
 * is three payments, and the question a person asks is "what has come in
 * against THIS invoice, and where is the proof". The Payments page answers
 * it per payment across every invoice; this answers it here, with a
 * "Record a payment" that already knows the customer and the invoice, so a
 * part-payment is one figure typed rather than a form filled. The status
 * badge above already says "Partially paid"; this is the list behind it.
 *
 * ── THE RECEIPT PER INSTALMENT ──
 * A receipt belongs to the payment it proves, not to the invoice - the
 * bank's UPI screenshot for the advance is not evidence of the balance.
 * Attachments hang off each payment (`ownerType=payment`, which the API and
 * the AI agent's attach tool already take), opened per row, with a count on
 * the row so the one that has none is visible at a glance.
 */

interface InvoicePayment {
  id: string;
  number: string;
  date: string;
  amount: string;
  applied: string;
  currency: string;
  mode: string;
  referenceNumber: string | null;
  notes: string | null;
  tdsSection: string | null;
  tdsAmount: string;
  bankCharges: string;
  receipts: number;
  /** The book's side of a foreign-currency payment (0021). */
  baseCurrency?: string;
  baseAmount?: string;
  exchangeRate?: string;
  appliedBase?: string;
  bookedBase?: string;
  fxDifference?: string;
}

export function InvoicePayments({
  invoice,
}: {
  invoice: {
    id: string;
    number: string;
    status: string;
    balanceDue: string;
    currency: string;
    customer?: { id: string } | null;
    writtenOffAmount?: string | null;
    writtenOffReason?: string | null;
  };
}) {
  const org = useActiveOrg();
  const perms = usePermissions();
  const [recording, setRecording] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [writingOff, setWritingOff] = useState(false);
  const [tdsOn, setTdsOn] = useState<InvoicePayment | null>(null);

  const payments = useQuery({
    queryKey: ['invoice-payments', org?.id, invoice.id],
    queryFn: () => api.get<{ items: InvoicePayment[] }>(`/payments/for-invoice/${invoice.id}`),
    enabled: !!org?.id && perms.can('payment', 'view'),
  });

  if (!perms.can('payment', 'view')) return null;
  const items = payments.data?.items ?? [];
  const canRecord =
    perms.can('payment', 'create') && !!invoice.customer?.id && invoice.status !== 'draft' && invoice.status !== 'void' && Number(invoice.balanceDue) > 0;
  const isZero = (value: string | null | undefined) => !value || Number(value) === 0;
  const owed = Number(invoice.balanceDue) > 0 && invoice.status !== 'draft' && invoice.status !== 'void' && invoice.status !== 'written_off';
  // The two ways the last of a balance closes without a payment. Write-off
  // restates a receivable, so it needs the same permission as a void.
  const canWriteOff = owed && perms.can('invoice', 'manage');
  const canRecordTds = (row: InvoicePayment) => owed && perms.can('payment', 'edit') && isZero(row.tdsAmount);
  const writtenOff = !isZero(invoice.writtenOffAmount);

  return (
    <Card className="print:hidden" data-testid="invoice-payments">
      <CardContent className="pt-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Receipt className="size-4 text-muted-foreground" aria-hidden />
            Payments
            {items.length ? <Badge variant="info">{items.length}</Badge> : null}
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            {canWriteOff ? (
              <Button type="button" size="sm" variant="ghost" onClick={() => setWritingOff(true)} data-testid="invoice-write-off">
                <Scissors className="size-4" aria-hidden />
                Write off <Money value={invoice.balanceDue} currency={invoice.currency} />
              </Button>
            ) : null}
            {canRecord ? (
              <Button type="button" size="sm" variant="outline" onClick={() => setRecording(true)} data-testid="invoice-record-payment">
                <Plus className="size-4" aria-hidden />
                Record a payment
              </Button>
            ) : null}
          </div>
        </div>
        {writtenOff ? (
          <p className="mb-3 flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2 text-sm" data-testid="invoice-written-off">
            <Scissors className="size-4 text-muted-foreground" aria-hidden />
            <span>
              <Money value={invoice.writtenOffAmount ?? '0'} currency={invoice.currency} /> written off
              {invoice.writtenOffReason === 'rounding' ? ' as rounding' : invoice.writtenOffReason === 'bad_debt' ? ' as bad debt' : ''}. The sale stays on the
              books; that part was never received.
            </span>
          </p>
        ) : null}

        {payments.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="invoice-payments-empty">
            Nothing received against this invoice yet.
            {canRecord ? ' A part-payment is fine - record what came in, attach its receipt, and the balance follows.' : ''}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((row) => {
              const withheld = !isZero(row.tdsAmount) || !isZero(row.bankCharges);
              const showing = open === row.id;
              return (
                <li key={row.id} className="py-3 first:pt-0 last:pb-0" data-testid="invoice-payment-row">
                  <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">
                        {row.number} <span className="font-normal text-muted-foreground">· {formatDate(row.date)} · {row.mode.replace(/_/g, ' ')}</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {row.referenceNumber?.trim() || row.notes?.trim() || ''}
                        {withheld ? (
                          <span className="ml-1" data-testid="invoice-payment-withheld">
                            {!isZero(row.tdsAmount) ? `TDS ${row.tdsSection ?? ''} ${row.tdsAmount}` : ''}
                            {!isZero(row.tdsAmount) && !isZero(row.bankCharges) ? ' · ' : ''}
                            {!isZero(row.bankCharges) ? `bank charges ${row.bankCharges}` : ''}
                          </span>
                        ) : null}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <Money value={row.applied} currency={row.currency} className="text-sm font-semibold" />
                        {row.applied !== row.amount ? (
                          <p className="text-xs text-muted-foreground">
                            of <Money value={row.amount} currency={row.currency} /> received
                          </p>
                        ) : null}
                        {/* A foreign payment: what the bank was credited, the
                            rate it implies, and the exchange difference this
                            allocation realised against the invoice's rate. */}
                        {row.baseCurrency && row.baseCurrency !== row.currency && row.baseAmount ? (
                          <p className="text-xs text-muted-foreground" data-testid="invoice-payment-base">
                            <Money value={row.baseAmount} currency={row.baseCurrency} /> credited at {formatRateHuman(row.exchangeRate ?? '0')}
                            {row.fxDifference && Number(row.fxDifference) !== 0 ? (
                              <>
                                {' · '}
                                exchange {Number(row.fxDifference) > 0 ? 'gain' : 'loss'}{' '}
                                <Money value={row.fxDifference} currency={row.baseCurrency} colorNegative />
                              </>
                            ) : null}
                          </p>
                        ) : null}
                      </div>
                      {canRecordTds(row) ? (
                        <Button type="button" variant="ghost" size="sm" onClick={() => setTdsOn(row)} data-testid="invoice-payment-tds">
                          <Landmark className="size-3.5" aria-hidden />
                          TDS deducted?
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        variant={row.receipts ? 'ghost' : 'outline'}
                        size="sm"
                        onClick={() => setOpen(showing ? null : row.id)}
                        aria-expanded={showing}
                        data-testid="invoice-payment-receipt"
                      >
                        <Paperclip className="size-3.5" aria-hidden />
                        {row.receipts ? `${row.receipts === 1 ? 'Receipt' : `${row.receipts} receipts`}` : 'Attach receipt'}
                      </Button>
                    </div>
                  </div>
                  {showing ? (
                    <div className="mt-2">
                      <Attachments ownerType="payment" ownerId={row.id} title="Receipt" emptyHint="The bank's confirmation, a UPI screenshot, the cheque - proof of this payment." />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      {writingOff ? <WriteOffSheet invoice={invoice} onClose={() => setWritingOff(false)} /> : null}
      {tdsOn ? <PaymentTdsSheet invoiceId={invoice.id} payment={tdsOn} currency={invoice.currency} balanceDue={invoice.balanceDue} onClose={() => setTdsOn(null)} /> : null}
      {recording && invoice.customer?.id ? (
        <RecordPaymentDrawer
          onClose={() => setRecording(false)}
          prefill={{ customerId: invoice.customer.id, invoiceId: invoice.id, balanceDue: invoice.balanceDue, currency: invoice.currency }}
          onRecorded={(payment) => setOpen(payment.id)}
        />
      ) : null}
    </Card>
  );
}
